"use client";

import { Search, X } from "lucide-react";
import { useSearch } from "@/hooks/use-search";
import SearchResults from "./search-results";
import { SearchResult } from "@/store/search-store";
import { useEffect, useState } from "react";

interface SearchBarProps {
  searchAction?: (query: string) => Promise<{
    success?: boolean;
    results?: SearchResult[];
  }>;
  onExpandChange?: (expanded: boolean) => void;
  companyId?: string;
}

export function SearchBar({
  searchAction,
  onExpandChange,
  companyId,
}: SearchBarProps) {
  const {
    query,
    results,
    isLoading,
    error,
    recentSearches,
    isOpen,
    searchInputRef,
    searchResultsRef,
    handleSearch,
    handleItemClick,
    handleRecentSearchClick,
    clearRecentSearches,
    openSearch,
    closeSearch,
  } = useSearch(searchAction, companyId);

  const [showContent, setShowContent] = useState(false);

  // Notify parent component about expansion state changes
  useEffect(() => {
    if (onExpandChange) {
      onExpandChange(isOpen);
    }
  }, [isOpen, onExpandChange]);

  // Handle content visibility with delay
  useEffect(() => {
    if (isOpen) {
      // Show content after 200ms delay when opening
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 200);
      return () => clearTimeout(timer);
    } else {
      // Hide content immediately when closing
      setShowContent(false);
    }
  }, [isOpen]);

  return (
    <div className='relative inline-flex h-9'>
      <div
        className={`absolute right-0 top-0 bottom-0 flex items-center transition-all duration-300 origin-right ${
          isOpen ? "w-[600px] border-b border-primary" : "w-9"
        }`}
      >
        <div
          className={`flex items-center ${
            isOpen
              ? "absolute left-3 top-1/2 -translate-y-1/2"
              : "h-full w-full justify-center p-2 cursor-pointer hover:text-primary"
          }`}
          onClick={!isOpen ? openSearch : undefined}
        >
          <Search
            className={`${
              isOpen ? "h-4 w-4 text-muted-foreground" : "h-5 w-5"
            }`}
          />
        </div>

        {isOpen && showContent && (
          <>
            <input
              ref={searchInputRef}
              type='text'
              placeholder='Search products...'
              className='pl-10 pr-10 h-9 w-full text-sm bg-transparent text-foreground focus:outline-none border-0 animate-in fade-in duration-300'
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              autoFocus
            />

            <button
              className='flex-shrink-0 h-9 px-2 flex items-center'
              onClick={closeSearch}
              aria-label='Close search'
            >
              <X className='h-4 w-4 text-muted-foreground hover:text-foreground' />
            </button>

            <SearchResults
              query={query}
              results={results}
              isLoading={isLoading}
              error={error}
              recentSearches={recentSearches}
              onItemClick={handleItemClick}
              onRecentSearchClick={handleRecentSearchClick}
              onClearRecentSearches={clearRecentSearches}
              ref={searchResultsRef}
            />
          </>
        )}
      </div>
    </div>
  );
}
